/**
 * Downscale a large image on the client before upload so it fits well under
 * the backend size cap. We only touch images that are actually large — small
 * PNGs, GIFs, and already-optimised JPEGs pass through untouched.
 *
 * Strategy:
 *   - If the file is <= threshold bytes → return as-is (no re-encode).
 *   - Otherwise decode into a canvas, scale so the longest edge is <= maxEdge,
 *     and re-encode as JPEG at the given quality.
 *   - GIFs pass through untouched (animation would be lost by canvas).
 */
export async function downscaleImage(
  file,
  { thresholdBytes = 3 * 1024 * 1024, maxEdge = 2400, quality = 0.85 } = {}
) {
  if (!file || !file.type || !file.type.startsWith('image/')) return file;
  if (file.type === 'image/gif') return file;
  if (file.size <= thresholdBytes) return file;

  const bitmap = await loadBitmap(file);
  const { width, height } = bitmap;
  const scale = Math.min(1, maxEdge / Math.max(width, height));
  const outW = Math.round(width * scale);
  const outH = Math.round(height * scale);

  const canvas = document.createElement('canvas');
  canvas.width = outW;
  canvas.height = outH;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(bitmap, 0, 0, outW, outH);
  // Free the bitmap resources
  if (typeof bitmap.close === 'function') bitmap.close();

  const blob = await new Promise((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error('Canvas encode failed'))),
      'image/jpeg',
      quality
    );
  });

  // If we somehow made it bigger, just return the original
  if (blob.size >= file.size) return file;

  const newName = file.name.replace(/\.[^.]+$/, '') + '.jpg';
  return new File([blob], newName, { type: 'image/jpeg', lastModified: Date.now() });
}

async function loadBitmap(file) {
  if (typeof createImageBitmap === 'function') {
    try {
      return await createImageBitmap(file);
    } catch (_) {
      // Fall through to the <img> path
    }
  }
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = () => reject(new Error('Image decode failed'));
      i.src = url;
    });
    return img;
  } finally {
    URL.revokeObjectURL(url);
  }
}
